/**
 * `useInstallPrompt` (offline-and-pwa.md §7). Captures the `beforeinstallprompt` event
 * so the app can offer an in-app "Install ARGUS" affordance at a moment of our choosing
 * (the browser's default mini-infobar is suppressed via `preventDefault`). On iOS Safari
 * — which has no prompt API — `canInstall` stays false and the UI falls back to the
 * "Add to Home Screen" help copy (§7).
 */
import { useCallback, useEffect, useState } from 'react';

/** The non-standard install-prompt event (not in lib.dom yet); minimal shape we use. */
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

export interface InstallPrompt {
  /** True once the browser has offered an installable event and the app is not yet installed. */
  canInstall: boolean;
  /** Trigger the native install prompt. No-op when nothing is deferred. */
  promptInstall: () => Promise<void>;
}

export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event): void {
      // Suppress the default mini-infobar; surface our own button instead (§7).
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    }
    function onAppInstalled(): void {
      setInstalled(true);
      setDeferred(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    if (!deferred) return;
    await deferred.prompt();
    // A prompt can only be used once; drop it regardless of the user's choice.
    setDeferred(null);
  }, [deferred]);

  return { canInstall: deferred !== null && !installed, promptInstall };
}
